import React, { useEffect, useState, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import authService from '../services/authService';
import { useNavigate, useParams } from 'react-router-dom';
import { buildApiUrl, API_CONFIG } from '../config/api';

function BlogWriting() {
    const [content, setContent] = useState('');
    const [blogHeader, setBlogHeader] = useState('');
    const [date, setDate] = useState('');
    const [author, setAuthor] = useState('Akseli Rajahalme');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [postId, setPostId] = useState(null);
    const [loadingPost, setLoadingPost] = useState(false);
    const editorRef = useRef(null);
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        // Check authentication status
        const checkAuth = () => {
            try {
                const user = authService.getCurrentUser();
                const isAuth = localStorage.getItem('isAuthenticated') === 'true';
                
                console.log('BlogWriting auth check:', { user, isAuth });
                
                if (!user || !isAuth) {
                    console.log('Not authenticated, redirecting to login');
                    // Not authenticated, redirect to login
                    navigate('/login');
                    return;
                }
                console.log('Authentication successful, setting state');
                setIsAuthenticated(true);
                setCheckingAuth(false);
            } catch (error) {
                console.error('Auth check error:', error);
                // Clear any invalid data and redirect to login
                localStorage.removeItem('user');
                localStorage.removeItem('isAuthenticated');
                localStorage.removeItem('csrf_token');
                navigate('/login');
            }
        };

        checkAuth();
    }, [navigate]);

    // Load post data if in edit mode
    useEffect(() => {
        if (id && isAuthenticated) {
            setIsEditMode(true);
            setPostId(id);
            loadPostData(id);
        }
    }, [id, isAuthenticated]);

    const loadPostData = async (postId) => {
        setLoadingPost(true);
        try {
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.GET_POST, { id: postId }), {
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                setBlogHeader(data.title || '');
                setContent(data.content || '');
                setAuthor(data.author || 'Akseli Rajahalme');
                setDate(data.created_at || '');
            } else {
                setMessage('Error: Failed to load post data');
            }
        } catch (error) {
            console.error('Error loading post:', error);
            setMessage('Error: Failed to load post data');
        } finally {
            setLoadingPost(false);
        }
    };

    useEffect(() => {
        const shouldScroll = sessionStorage.getItem('scrollToContact');
        if (shouldScroll === 'true') {
            const contactSection = document.getElementById('contact');
            if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
            }
            sessionStorage.removeItem('scrollToContact');
        }
    }, []);



    const handleSavePost = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');

        try {
            const postData = {
                title: blogHeader,
                content: content,
                author: author,
                published: 0 // Save as draft
            };

            // Add ID if in edit mode
            if (isEditMode && postId) {
                postData.id = postId;
            }

            const response = isEditMode ? 
                await authService.updateBlogPost(postData) : 
                await authService.createBlogPost(postData);
            const result = await response.json();

            if (response.ok) {
                setMessage(isEditMode ? 'Post updated successfully as draft!' : 'Post saved successfully as draft!');
                // Clear form only if creating new post
                if (!isEditMode) {
                    setBlogHeader('');
                    setContent('');
                    setDate('');
                }
            } else {
                setMessage(`Error: ${result.message || 'Failed to save post'}`);
            }
        } catch (error) {
            console.error('Error saving post:', error);
            if (error.message.includes('Session expired')) {
                setMessage('Session expired. Please login again.');
                // Redirect to login
                window.location.href = '/login';
            } else {
                setMessage('Error: Failed to save post. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePublishPost = async () => {
        setLoading(true);
        setMessage('');

        // Validate required fields
        if (!blogHeader.trim()) {
            setMessage('Error: Please enter a title for your blog post.');
            setLoading(false);
            return;
        }

        if (!content.trim()) {
            setMessage('Error: Please enter some content for your blog post.');
            setLoading(false);
            return;
        }

        try {
            const postData = {
                title: blogHeader.trim(),
                content: content,
                author: author,
                published: 1 // Publish immediately
            };

            // Add ID if in edit mode
            if (isEditMode && postId) {
                postData.id = postId;
            }

            // Debug logging
            console.log('Publishing post with data:', {
                title: postData.title,
                contentLength: postData.content ? postData.content.length : 0,
                author: postData.author,
                published: postData.published,
                id: postData.id,
                hasImages: postData.content && postData.content.includes('<img'),
                hasLinks: postData.content && postData.content.includes('<a ')
            });

            const response = isEditMode ? 
                await authService.updateBlogPost(postData) : 
                await authService.createBlogPost(postData);
            const result = await response.json();

            if (response.ok) {
                setMessage(isEditMode ? 'Post updated and published successfully!' : 'Post published successfully!');
                // Clear form only if creating new post
                if (!isEditMode) {
                    setBlogHeader('');
                    setContent('');
                    setDate('');
                }
                // Set flag to refresh dashboard
                sessionStorage.setItem('refreshDashboard', 'true');
            } else {
                setMessage(`Error: ${result.message || 'Failed to publish post'}`);
            }
        } catch (error) {
            console.error('Error publishing post:', error);
            console.error('Full error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
            
            if (error.message.includes('Session expired')) {
                setMessage('Session expired. Please login again.');
                // Redirect to login
                window.location.href = '/login';
            } else if (error.message.includes('Network error')) {
                setMessage(`Network error: ${error.message}`);
            } else {
                setMessage(`Error: ${error.message || 'Failed to publish post. Please try again.'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Show loading while checking authentication
    if (checkingAuth) {
        return (
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-11 px-2 px-md-1">
                        <div className="text-center">
                            <p>Checking authentication...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show loading while loading post data
    if (loadingPost) {
        return (
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-11 px-2 px-md-1">
                        <div className="text-center">
                            <p>Loading post data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // If not authenticated, don't render anything (will redirect to login)
    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="Blogs-wrapper container mt-5">
            <div className="row justify-content-center">
                <div className="col-11 px-2 px-md-1">

                    {/* Header */}
                    <div className="header-section">
                        <div>
                            <h1 className='header-name mb-4 mb-md-3'>{isEditMode ? 'Edit post' : 'New post'}</h1>
                            {message && (
                                <div className={`alert ${message.includes('Error') ? 'alert-danger' : 'alert-success'} mb-3`}>
                                    {message}
                                </div>
                            )}
                            <div className="d-flex justify-content-between pt-4 pt-sm-5">
                                <div>
                                    <button
                                        type="button"
                                        className="btn me-2"
                                        onClick={() => window.location.href = '/dashboard'}
                                    >
                                        Back
                                    </button>
                                </div>
                                <div className='d-flex gap-3'>
                                    <button
                                        type="submit"
                                        className="btn"
                                        form="blog-form"
                                        disabled={loading}
                                    >
                                        {loading ? 'Saving...' : 'Save Post'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn"
                                        style={{ marginLeft: 8 }}
                                        onClick={handlePublishPost}
                                        disabled={loading}
                                    >
                                        {loading ? 'Publishing...' : 'Publish post'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Blog Form */}
                    <form id="blog-form" onSubmit={handleSavePost}>
                        <div style={{ marginBottom: 16 }}>
                            <label className="subtext">
                                Blog header:
                                <input
                                    className="form-control"
                                    type="text"
                                    value={blogHeader}
                                    onChange={e => setBlogHeader(e.target.value)}
                                    style={{ width: "100%", padding: 8, marginTop: 4 }}
                                    required
                                />
                            </label>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label className="subtext">
                                Author:
                                <input
                                    className="form-control"
                                    type="text"
                                    value={author}
                                    onChange={e => setAuthor(e.target.value)}
                                    style={{ width: "100%", padding: 8, marginTop: 4 }}
                                    required
                                />
                            </label>
                        </div>

                        {/* Text Editor */}
                        <div className="EditorSection mt-4 mb-5">
                            <label className="subtext">Blog content:</label>
                            <Editor
                                apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                onInit={(evt, editor) => editorRef.current = editor}
                                value={content}
                                onEditorChange={(content, editor) => setContent(content)}
                                init={{
                                    height: 400,
                                    menubar: false,
                                    plugins: [
                                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                                    ],
                                    toolbar: 'undo redo | blocks | ' +
                                        'bold italic forecolor | alignleft aligncenter ' +
                                        'alignright alignjustify | bullist numlist outdent indent | ' +

                                        'removeformat | link image | help',
                                    content_style: 'body { font-family: Montserrat, sans-serif; font-size: 14px; }',
                                    skin: 'oxide-dark',
                                    content_css: 'dark',
                                    // Image configuration
                                    image_advtab: true,
                                    image_description: false,
                                    image_title: true,
                                    // Link configuration
                                    link_assume_external_targets: true,
                                    link_default_target: '_blank',
                                    link_default_protocol: 'https',
                                    // Security settings
                                    valid_elements: 'p,br,strong,em,u,h1,h2,h3,h4,h5,h6,ul,ol,li,blockquote,code,pre,img[src|alt|width|height],a[href|target|rel]',
                                    valid_children: '+body[style]',
                                    // Image upload settings
                                    images_upload_handler: function (blobInfo, success, failure) {
                                        // Convert to base64 but limit size
                                        const reader = new FileReader();
                                        reader.onload = function() {
                                            let base64 = reader.result;
                                            
                                            // Check if image is too large (limit to 1MB)
                                            if (base64.length > 1000000) {
                                                // Resize the image
                                                const img = new Image();
                                                img.onload = function() {
                                                    const canvas = document.createElement('canvas');
                                                    const ctx = canvas.getContext('2d');
                                                    
                                                    // Calculate new dimensions (max 800px width)
                                                    let { width, height } = img;
                                                    if (width > 800) {
                                                        height = (height * 800) / width;
                                                        width = 800;
                                                    }
                                                    
                                                    canvas.width = width;
                                                    canvas.height = height;
                                                    ctx.drawImage(img, 0, 0, width, height);
                                                    
                                                    const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
                                                    success(resizedBase64);
                                                };
                                                img.src = base64;
                                            } else {
                                                success(base64);
                                            }
                                        };
                                        reader.readAsDataURL(blobInfo.blob());
                                    }
                                }}
                            />
                        </div>
                    </form>

                </div>
            </div>
        </div>
    );
}

export default BlogWriting;